//Firestore DB info/service file

import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch,
  runTransaction,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { User, Animal, UserPresence, Forest, DailyExercise, Tree, WaterAction, ExerciseCompletion } from '../types';
import { GAME_CONFIG } from '../utils/gameConfig';
import { tracedOnSnapshot } from './firestoreDebug';




/************ DEBUG -- TO DELETE *********** */


/*One*/

export const checkAllActivePresence = async (): Promise<void> => {
  try {
    console.log('=== CHECKING ALL ACTIVE PRESENCE ===');
    
    const forestsRef = collection(db, 'forests');
    const activeForests = await getDocs(query(forestsRef, where('isActive', '==', true)));
    
    console.log(`Found ${activeForests.size} active forests`);
    
    let totalActiveUsers = 0;
    let staleUsers = 0;
    
    for (const forestDoc of activeForests.docs) {
      const forestId = forestDoc.id;
      const forestName = forestDoc.data().name;
      
      const presenceRef = collection(db, 'forests', forestId, 'userPresence');
      const activeUsers = await getDocs(presenceRef);
      
      if (!activeUsers.empty) {
        console.log(`\nForest "${forestName}" (${forestId}):`);
        activeUsers.docs.forEach(doc => {
          const data = doc.data();
          const lastUpdate = data.lastUpdate?.toDate() || new Date(0);
          const minutesAgo = Math.round((Date.now() - lastUpdate.getTime()) / 60000);
          
          totalActiveUsers++;
          
          console.log(`  - ${data.username} (${doc.id}): Last seen ${minutesAgo} minutes ago`);
          
          if (minutesAgo > 5) {
            console.warn(`    ⚠️ STALE SESSION!`);
            staleUsers++;
          }
        });
      }
    }
    
    console.log('\n=== SUMMARY ===');
    console.log(`Total active users: ${totalActiveUsers}`);
    console.log(`Stale users (>5 min): ${staleUsers}`);
    console.log('=== END CHECK ===');
    
  } catch (error) {
    console.error('Error checking presence:', error);
  }
};

/*two*/




const activeListeners = new Map<string, string>();

//Creating listeners
export const trackListener = (id: string, description: string) => {
  activeListeners.set(id, description);
  console.log(`Listener registered: ${id} - ${description}`);
};

//Removing listeners
export const untrackListener = (id: string) => {
  activeListeners.delete(id);
  console.log(`Listener unregistered: ${id}`);
};

//Debug
export const debugFirestoreReads = async () => {
  console.log('===***** FIREBASE DEBUG INFO *****===');
  
  console.log('Current auth state:', {
    currentUser: auth.currentUser?.uid,
    currentUserEmail: auth.currentUser?.email,
  });
  
  console.log('Tracked Firestore listeners:', {
    count: activeListeners.size,
    details: Array.from(activeListeners.entries())
  });
  
  console.log('LocalStorage auth data:', {
    hasAuthData: !!localStorage.getItem('firebase:authUser:' + process.env.REACT_APP_FIREBASE_API_KEY),
  });
  
  console.log('===***** END DEBUG INFO *****===');
};

/************* END DEBUG. *********** */



/******** USERS  **********/

export const createUser = async (uid: string, userData: Omit<User, 'uid'>): Promise<User> => {
  const userRef = doc(db, 'users', uid);
  
  //Class code normalization
  const normalizedUserData = { ...userData };
  
  if (userData.classCode) {
    normalizedUserData.classCode = userData.classCode.trim().toUpperCase();
  }
  
  const user: User = { uid, ...normalizedUserData };
  
  await setDoc(userRef, {
    ...user,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  
  //Update forest student count
  if (user.userType === 'student' && user.classCode) {
    await updateForestStudentCount(user.classCode);
  }
  
  console.log('User created:', {
    uid,
    userType: user.userType,
    classCode: user.classCode || 'None (instructor)',
    name: `${user.firstName} ${user.lastName}`
  });
  
  return user;
};

export const getUserByUid = async (uid: string): Promise<User | null> => {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    const data = userSnap.data();
    return {
      uid,
      ...data,
      userType: data.userType || 'student', //Existing users default to student
      isActive: data.isActive !== undefined ? data.isActive : true, //Default to active for existing users
      preferredColor: data.preferredColor,
      lastExerciseDate: data.lastExerciseDate?.toDate() || null,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as User;
  }
  
  return null;
};

export const activateInstructor = async (instructorId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', instructorId);
    await updateDoc(userRef, {
      isActive: true,
      updatedAt: serverTimestamp(),
    });
    
    console.log('Instructor activated:', instructorId);
  } catch (error) {
    console.error('Error activating instructor:', error);
    throw error;
  }
};

export const updateUser = async (uid: string, updates: Partial<User>): Promise<void> => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const checkUsernameExists = async (username: string): Promise<boolean> => {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('username', '==', username));
  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
};


export const getUserByUsername = async (username: string): Promise<User | null> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('username', '==', username));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        uid: doc.id,
        ...data,
        userType: data.userType || 'student',
        isActive: data.isActive !== undefined ? data.isActive : true,
        preferredColor: data.preferredColor || data.chosenColor,
        lastExerciseDate: data.lastExerciseDate?.toDate() || null,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as User;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by username:', error);
    return null;
  }
};




export const updateForestStudentCount = async (classCode: string): Promise<void> => {
  try {
    console.log('Updating student count for class code:', classCode);
    
    //Find forests w/class code
    const forestsRef = collection(db, 'forests');
    const forestQuery = query(forestsRef, where('classCode', '==', classCode));
    const forestSnapshot = await getDocs(forestQuery);
    
    if (forestSnapshot.empty) {
      console.log('No forest found with class code:', classCode);
      return;
    }
    
    //Get student count
    const usersRef = collection(db, 'users');
    const studentQuery = query(
      usersRef,
      where('classCode', '==', classCode),
      where('userType', '==', 'student')
    );
    const studentSnapshot = await getDocs(studentQuery);
    const studentCount = studentSnapshot.docs.length;
    
    console.log(`Found ${studentCount} students for class code ${classCode}`);
    
    //Update
    const batch = writeBatch(db);
    forestSnapshot.docs.forEach(forestDoc => {
      batch.update(forestDoc.ref, {
        currentStudents: studentCount,
        updatedAt: serverTimestamp(),
      });
    });
    
    await batch.commit();
    console.log(`Updated ${forestSnapshot.docs.length} forests with student count: ${studentCount}`);
    
  } catch (error) {
    console.error('Error updating forest student count:', error);
  }
};



/******** FORESTS **********/

export const checkClassCodeExists = async (classCode: string): Promise<boolean> => {
  const forestsRef = collection(db, 'forests');
  const q = query(forestsRef, where('classCode', '==', classCode));
  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
};

export const getForestByClassCode = async (classCode: string): Promise<Forest | null> => {
  const forestsRef = collection(db, 'forests');
  const q = query(forestsRef, where('classCode', '==', classCode));
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Forest;
  }
  
  return null;
};


/******** TEACHER **********/

export const createForest = async (forestData: {
  name: string;
  slug: string;
  classCode: string;
  teacherId: string;
  dailyExerciseGroupId: string;
  gameDuration: number;
  startDate: Date;
  endDate: Date;
  maxStudents?: number | null;
  dailyWaterLimit?: number | null;
  studentWaterLimit?: number | null;
  gameWaterLimit?: number | null;
  isActive: boolean;
  currentStudents: number;
  isQuickGame?: boolean;
  timeMultiplier?: number;
  isDraft?: boolean;
}): Promise<Forest> => {
  try {
    const forestsRef = collection(db, 'forests');
    
    const docRef = await addDoc(forestsRef, {
      ...forestData,
      startDate: Timestamp.fromDate(forestData.startDate),
      endDate: Timestamp.fromDate(forestData.endDate),
      isDraft: forestData.isDraft || false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log('Forest created with ID:', docRef.id);
    
    return {
      id: docRef.id,
      ...forestData,
      isDraft: forestData.isDraft || false,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Forest;
  } catch (error) {
    console.error('Error creating forest:', error);
    throw error;
  }
};

export const getTeacherForests = async (teacherId: string): Promise<Forest[]> => {
  try {
    const forestsRef = collection(db, 'forests');
    const q = query(forestsRef, where('teacherId', '==', teacherId));
    const querySnapshot = await getDocs(q);
    
    const forests: Forest[] = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      console.log('Forest from DB:', {
        id: doc.id,
        name: data.name,
        isDraft: data.isDraft,
        isActive: data.isActive,
        rawData: data
      });
      
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate?.toDate() || new Date(),
        endDate: data.endDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        isDraft: data.isDraft || false,
        isQuickGame: data.isQuickGame || false,
        timeMultiplier: data.timeMultiplier || 1,
      };
    }) as Forest[];
    
    console.log(`Loaded ${forests.length} forests for teacher ${teacherId}:`, {
      total: forests.length,
      drafts: forests.filter(f => f.isDraft).length,
      active: forests.filter(f => f.isActive && !f.isDraft).length,
      completed: forests.filter(f => !f.isActive && !f.isDraft).length
    });
    
    return forests;
  } catch (error) {
    console.error('Error getting teacher forests:', error);
    throw error;
  }
};

export const getForestStudents = async (forestId: string): Promise<User[]> => {
  try {
    //Get class code
    const forestRef = doc(db, 'forests', forestId);
    const forestDoc = await getDoc(forestRef);
    
    if (!forestDoc.exists()) {
      throw new Error('Forest not found');
    }
    
    const forestData = forestDoc.data();
    const classCode = forestData.classCode;
    
    //Get all users
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef, 
      where('classCode', '==', classCode),
      where('userType', '==', 'student')
    );
    const querySnapshot = await getDocs(q);
    
    const students: User[] = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        uid: doc.id,
        ...data,
        lastExerciseDate: data.lastExerciseDate?.toDate() || null,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    }) as User[];
    
    console.log(`Loaded ${students.length} students for forest ${forestId}`);
    return students;
  } catch (error) {
    console.error('Error getting forest students:', error);
    throw error;
  }
};


export const getAllForestExerciseCompletions = async (forestId: string): Promise<Map<string, any[]>> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const querySnapshot = await getDocs(exerciseCompletionsRef);
    
    const completionsByStudent = new Map<string, any[]>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const studentId = data.userId;
      
      if (!completionsByStudent.has(studentId)) {
        completionsByStudent.set(studentId, []);
      }
      
      completionsByStudent.get(studentId)!.push({
        id: doc.id,
        ...data,
        completedAt: data.completedAt?.toDate() || new Date(),
      });
    });
    
    console.log(`Batch loaded exercises`);
    return completionsByStudent;
  } catch (error) {
    console.error('Error batch loading exercises:', error);
    return new Map();
  }
};

export const getAllForestWaterActions = async (forestId: string): Promise<Map<string, any[]>> => {
  try {
    const waterActionsRef = collection(db, 'forests', forestId, 'waterActions');
    const querySnapshot = await getDocs(waterActionsRef);
    
    const actionsByStudent = new Map<string, any[]>();
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const recipientId = data.toUserId;
      
      if (!actionsByStudent.has(recipientId)) {
        actionsByStudent.set(recipientId, []);
      }
      
      actionsByStudent.get(recipientId)!.push({
        id: doc.id,
        ...data,
        timestamp: data.timestamp?.toDate() || new Date(),
      });
    });
    
    console.log(`Batch loaded water actions`);
    return actionsByStudent;
  } catch (error) {
    console.error('Error batch loading water actions:', error);
    return new Map();
  }
};

export const updateStudentResources = async (
  studentId: string, 
  resourceType: 'seeds' | 'water', 
  amount: number
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', studentId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('Student not found');
    }
    
    const userData = userDoc.data();
    const currentAmount = userData[resourceType] || 0;
    const newAmount = Math.max(0, currentAmount + amount);
    
    await updateDoc(userRef, {
      [resourceType]: newAmount,
      updatedAt: serverTimestamp(),
    });
    
    console.log(`Updated ${resourceType} for student ${studentId}: ${currentAmount} -> ${newAmount} (${amount > 0 ? '+' : ''}${amount})`);
  } catch (error) {
    console.error('Error updating student resources:', error);
    throw error;
  }
};

export const getStudentExerciseCompletions = async (
  forestId: string, 
  studentId: string
): Promise<any[]> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const q = query(exerciseCompletionsRef, where('userId', '==', studentId));
    const querySnapshot = await getDocs(q);
    
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      completedAt: doc.data().completedAt?.toDate() || new Date(),
    }));
    
    return completions;
  } catch (error) {
    console.error('Error getting student exercise completions:', error);
    return [];
  }
};

export const getForestStats = async (forestId: string): Promise<{
  totalStudents: number;
  totalTrees: number;
  totalExerciseCompletions: number;
  totalWaterActions: number;
}> => {
  try {
    //Get all subcollection counts
    const [students, trees, exercises, waterActions] = await Promise.all([
      getForestStudents(forestId),
      getForestTrees(forestId),
      getDocs(collection(db, 'forests', forestId, 'exerciseCompletions')),
      getDocs(collection(db, 'forests', forestId, 'waterActions')),
    ]);
    
    return {
      totalStudents: students.length,
      totalTrees: trees.length,
      totalExerciseCompletions: exercises.docs.length,
      totalWaterActions: waterActions.docs.length,
    };
  } catch (error) {
    console.error('Error getting forest stats:', error);
    throw error;
  }
};


/******** EXERCISES**********/

export const recordExerciseCompletion = async (
  forestId: string,
  userId: string,
  exerciseId: string,
  day: number,
  uploadedFiles?: string[]
): Promise<void> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    
    await addDoc(exerciseCompletionsRef, {
      userId,
      exerciseId,
      day,
      uploadedFiles: uploadedFiles || [],
      completedAt: serverTimestamp(),
    });
    
    console.log(`Recorded exercise completion: User ${userId}, Day ${day}`);
  } catch (error) {
    console.error('Error recording exercise completion:', error);
    throw error;
  }
};

export const getExerciseCompletionsByStudent = async (
  forestId: string,
  userId: string
): Promise<any[]> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const q = query(
      exerciseCompletionsRef, 
      where('userId', '==', userId),
      orderBy('completedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      completedAt: doc.data().completedAt?.toDate() || new Date(),
    }));
  } catch (error) {
    console.error('Error getting exercise completions:', error);
    return [];
  }
};



export const createForestExercises = async (
  forestId: string,
  exercises: Array<{
    day: number;
    mediaType: 0 | 1 | 2 | 3 | 4 | 5;
    title: string;
    instructions: string;
    mediaUrl: string;
  }>
): Promise<string> => {
  try {
    const groupId = `forest_${forestId}_${Date.now()}`;
    
    const batch = writeBatch(db);
    
    exercises.forEach((exercise) => {
      const exerciseRef = doc(collection(db, 'dailyExercises'));
      const exerciseData = {
        groupId,
        day: exercise.day,
        mediaType: exercise.mediaType,
        mediaUrl: exercise.mediaUrl,
        title: exercise.title,
        instructions: exercise.instructions,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      batch.set(exerciseRef, exerciseData);
    });
    
    const forestRef = doc(db, 'forests', forestId);
    batch.update(forestRef, {
      dailyExerciseGroupId: groupId,
      updatedAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    console.log(`Created ${exercises.length} exercises for forest ${forestId} with group ID ${groupId}`);
    return groupId;
  } catch (error) {
    console.error('Error creating forest exercises:', error);
    throw error;
  }
};

/* Update exercises */
export const updateForestExercises = async (
  groupId: string,
  exercises: Array<{
    day: number;
    mediaType: 0 | 1 | 2 | 3 | 4 | 5;
    title: string;
    instructions: string;
    mediaUrl: string;
  }>
): Promise<void> => {
  try {
    //Delete existing exercises for this group
    const existingExercisesRef = collection(db, 'dailyExercises');
    const q = query(existingExercisesRef, where('groupId', '==', groupId));
    const existingSnapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    
    existingSnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    //Add new exercises
    exercises.forEach((exercise) => {
      const exerciseRef = doc(collection(db, 'dailyExercises'));
      const exerciseData = {
        groupId,
        day: exercise.day,
        mediaType: exercise.mediaType,
        mediaUrl: exercise.mediaUrl,
        title: exercise.title,
        instructions: exercise.instructions,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      batch.set(exerciseRef, exerciseData);
    });
    
    await batch.commit();
    
    console.log(`Updated ${exercises.length} exercises for group ${groupId}`);
  } catch (error) {
    console.error('Error updating forest exercises:', error);
    throw error;
  }
};

/* Get all exercises by group ID */
export const getForestExercises = async (groupId: string): Promise<DailyExercise[]> => {
  try {
    const exercisesRef = collection(db, 'dailyExercises');
    const q = query(
      exercisesRef, 
      where('groupId', '==', groupId),
      orderBy('day', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const exercises: DailyExercise[] = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    }) as DailyExercise[];
    
    return exercises;
  } catch (error) {
    console.error('Error getting forest exercises:', error);
    throw error;
  }
};

/* Get all exercise groups created by a teacher */
//ToDo: Create master or local exercise repository

export const getTeacherExerciseGroups = async (teacherId: string): Promise<Array<{
  groupId: string;
  forestName: string;
  exerciseCount: number;
  createdAt: Date;
}>> => {
  try {

    //Get all forests for this teacher
    const forestsRef = collection(db, 'forests');
    const forestsQuery = query(forestsRef, where('teacherId', '==', teacherId));
    const forestsSnapshot = await getDocs(forestsQuery);
    
    const exerciseGroups = [];
    
    for (const forestDoc of forestsSnapshot.docs) {
      const forestData = forestDoc.data();
      if (forestData.dailyExerciseGroupId) {

        //Get # of exercises in this group
        const exercisesRef = collection(db, 'dailyExercises');
        const exercisesQuery = query(
          exercisesRef, 
          where('groupId', '==', forestData.dailyExerciseGroupId)
        );
        const exercisesSnapshot = await getDocs(exercisesQuery);
        
        exerciseGroups.push({
          groupId: forestData.dailyExerciseGroupId,
          forestName: forestData.name,
          exerciseCount: exercisesSnapshot.docs.length,
          createdAt: forestData.createdAt?.toDate() || new Date(),
        });
      }
    }
    
    return exerciseGroups.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    console.error('Error getting teacher exercise groups:', error);
    throw error;
  }
};

/* Duplicate Exercises */
export const duplicateExerciseGroup = async (
  sourceGroupId: string,
  newForestId: string
): Promise<string> => {
  try {

    //Get source exercises
    const sourceExercises = await getForestExercises(sourceGroupId);
    
    //Create new group ID
    const newGroupId = `forest_${newForestId}_${Date.now()}`;
    
    //Create new exercises with new group ID
    const newExercises = sourceExercises.map(exercise => ({
      day: exercise.day,
      mediaType: exercise.mediaType,
      title: exercise.title,
      instructions: exercise.instructions || '',
      mediaUrl: exercise.mediaUrl,
    }));
    
    await createForestExercises(newForestId, newExercises);
    
    console.log(`Duplicated ${newExercises.length} exercises from ${sourceGroupId} to ${newGroupId}`);
    return newGroupId;
  } catch (error) {
    console.error('Error duplicating exercise group:', error);
    throw error;
  }
};

/* Delete all exercises for a forest when forest is deleted */
export const deleteForestExercises = async (groupId: string): Promise<void> => {
  try {
    const exercisesRef = collection(db, 'dailyExercises');
    const q = query(exercisesRef, where('groupId', '==', groupId));
    const querySnapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    querySnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    
    console.log(`Deleted ${querySnapshot.docs.length} exercises for group ${groupId}`);
  } catch (error) {
    console.error('Error deleting forest exercises:', error);
    throw error;
  }
};



//Get all completed exercises for dashboard
export const getForestExerciseCompletions = async (forestId: string): Promise<ExerciseCompletion[]> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const q = query(exerciseCompletionsRef);
    const querySnapshot = await getDocs(q);
    
    const completions: ExerciseCompletion[] = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        forestId: forestId,
        exerciseId: data.exerciseId,
        exerciseDay: data.day || data.exerciseDay,
        completedAt: data.completedAt?.toDate() || new Date(),
        seedsEarned: data.seedsEarned || 1,
        createdAt: data.createdAt?.toDate() || new Date(),
      };
    });
    
    console.log(`Loaded ${completions.length} exercise completions for forest ${forestId}`);
    return completions;
  } catch (error) {
    console.error('Error getting forest exercise completions:', error);
    throw error;
  }
};

//Get student uploads
export const getStudentUploadedFiles = async (
  forestId: string, 
  studentId: string, 
  exerciseId: string
): Promise<string[]> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const q = query(
      exerciseCompletionsRef,
      where('userId', '==', studentId),
      where('exerciseId', '==', exerciseId)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return data.uploadedFiles || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting student uploaded files:', error);
    return [];
  }
};

//Bulk update (student resources)
export const bulkUpdateStudentResources = async (
  updates: Array<{
    studentId: string;
    seeds?: number;
    water?: number;
  }>
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    for (const update of updates) {
      const userRef = doc(db, 'users', update.studentId);
      const updateData: any = {
        updatedAt: serverTimestamp(),
      };
      
      if (update.seeds !== undefined) {
        updateData.seeds = update.seeds;
      }
      if (update.water !== undefined) {
        updateData.water = update.water;
      }
      
      batch.update(userRef, updateData);
    }
    
    await batch.commit();
    console.log(`Bulk updated resources for ${updates.length} students`);
  } catch (error) {
    console.error('Error bulk updating student resources:', error);
    throw error;
  }
};

//Get forest analytics
export const getForestAnalytics = async (forestId: string): Promise<{
  totalStudents: number;
  totalSeedsEarned: number;
  totalWaterEarned: number;
  totalSeedsPlanted: number;
  totalWaterSpent: number;
  exerciseCompletionRate: number;
  collaborationRate: number;
  treeMaturityDistribution: { [stage: number]: number };
  dailyActivityStats: { day: number; completions: number }[];
}> => {
  try {
    const [students, trees, exerciseCompletions] = await Promise.all([
      getForestStudents(forestId),
      getForestTrees(forestId),
      getForestExerciseCompletions(forestId)
    ]);

    //Calculate totals
    const totalSeedsEarned = students.reduce((sum, s) => sum + s.totalSeedsEarned, 0);
    const totalWaterEarned = students.reduce((sum, s) => sum + s.totalWaterEarned, 0);
    const totalSeedsPlanted = students.reduce((sum, s) => sum + s.totalSeedsPlanted, 0);
    const totalWaterSpent = students.reduce((sum, s) => sum + s.totalWaterUsedOnOthers, 0);

    //Calculate exercise completion rate
    const forest = await getForestByIdDirect(forestId);
    let expectedCompletions = 0;
    if (forest) {
      const exercises = await getDailyExercisesByGroup(forest.dailyExerciseGroupId);
      expectedCompletions = students.length * exercises.length;
    }
    const exerciseCompletionRate = expectedCompletions > 0 
      ? Math.round((exerciseCompletions.length / expectedCompletions) * 100)
      : 0;

    //Calculate collaboration rate
    const collaborationRate = totalWaterEarned > 0 
      ? Math.round((totalWaterSpent / totalWaterEarned) * 100)
      : 0;

    //Tree maturity distribution
    const treeMaturityDistribution: { [stage: number]: number } = {};
    for (let stage = 0; stage <= 5; stage++) {
      treeMaturityDistribution[stage] = trees.filter(tree => tree.maturityStage === stage).length;
    }

    //Daily activity stats (group completions by exercise day)
    const dailyActivityStats: { day: number; completions: number }[] = [];
    const dayCompletions: { [day: number]: number } = {};
    
    exerciseCompletions.forEach(completion => {
      const day = completion.exerciseDay;
      dayCompletions[day] = (dayCompletions[day] || 0) + 1;
    });

    Object.entries(dayCompletions).forEach(([day, completions]) => {
      dailyActivityStats.push({ day: parseInt(day), completions });
    });

    return {
      totalStudents: students.length,
      totalSeedsEarned,
      totalWaterEarned,
      totalSeedsPlanted,
      totalWaterSpent,
      exerciseCompletionRate,
      collaborationRate,
      treeMaturityDistribution,
      dailyActivityStats: dailyActivityStats.sort((a, b) => a.day - b.day)
    };
  } catch (error) {
    console.error('Error getting forest analytics:', error);
    throw error;
  }
};

//Helper function--get forest by ID
const getForestByIdDirect = async (forestId: string): Promise<Forest | null> => {
  try {
    const forestRef = doc(db, 'forests', forestId);
    const forestDoc = await getDoc(forestRef);
    
    if (!forestDoc.exists()) {
      return null;
    }
    
    const data = forestDoc.data();
    return {
      id: forestDoc.id,
      ...data,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Forest;
  } catch (error) {
    console.error('Error getting forest by ID:', error);
    return null;
  }
};

//Get collaboration stats
export const getStudentCollaborationStats = async (forestId: string): Promise<Array<{
  studentId: string;
  studentName: string;
  waterGiven: number;
  waterReceived: number;
  collaborationScore: number;
  treesWatered: number;
  uniqueRecipientsHelped: number;
}>> => {
  try {
    const [students, waterActions] = await Promise.all([
      getForestStudents(forestId),
      getDocs(collection(db, 'forests', forestId, 'waterActions'))
    ]);

    const waterActionsList = waterActions.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        fromUserId: data.fromUserId,
        fromUserName: data.fromUserName,
        toUserId: data.toUserId,
        treeId: data.treeId,
        amount: data.amount,
        timestamp: data.timestamp?.toDate() || new Date(),
      };
    });

    const collaborationStats = students.map(student => {
      //Water given by ONE student
      const waterGiven = waterActionsList
        .filter(action => action.fromUserId === student.uid)
        .reduce((sum, action) => sum + action.amount, 0);

      //Water received by ONE student
      const waterReceived = waterActionsList
        .filter(action => action.toUserId === student.uid && action.fromUserId !== student.uid)
        .reduce((sum, action) => sum + action.amount, 0);

      //Trees watered by ONE student
      const treesWatered = new Set(
        waterActionsList
          .filter(action => action.fromUserId === student.uid)
          .map(action => action.treeId)
      ).size;

      //Unique recipients watered
      const uniqueRecipientsHelped = new Set(
        waterActionsList
          .filter(action => action.fromUserId === student.uid && action.toUserId !== student.uid)
          .map(action => action.toUserId)
      ).size;

      //Collaboration score (percentage of water shared vs. earned)
      const totalWaterEarned = student.totalWaterEarned;
      const collaborationScore = totalWaterEarned > 0 
        ? Math.round((waterGiven / totalWaterEarned) * 100)
        : 0;

      return {
        studentId: student.uid,
        studentName: `${student.firstName} ${student.lastName}`,
        waterGiven,
        waterReceived,
        collaborationScore,
        treesWatered,
        uniqueRecipientsHelped,
      };
    });

    return collaborationStats.sort((a, b) => b.collaborationScore - a.collaborationScore);
  } catch (error) {
    console.error('Error getting student collaboration stats:', error);
    throw error;
  }
};

//Get all water actions for forest
export const getForestWaterActions = async (forestId: string): Promise<WaterAction[]> => {
  try {
    const waterActionsCollection = collection(db, 'forests', forestId, 'waterActions');
    const querySnapshot = await getDocs(waterActionsCollection);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        fromUserId: data.fromUserId,
        fromUserName: data.fromUserName,
        toUserId: data.toUserId,
        treeId: data.treeId,
        amount: data.amount,
        timestamp: data.timestamp?.toDate() || new Date(),
      };
    }) as WaterAction[];
  } catch (error) {
    console.error('Error getting forest water actions:', error);
    throw error;
  }
};

//Get student exercise completions
export const getStudentExerciseCompletionsDetailed = async (
  forestId: string, 
  studentId: string
): Promise<Array<{
  id: string;
  exerciseId: string;
  exerciseDay: number;
  exerciseTitle?: string;
  completedAt: Date;
  uploadedFiles: string[];
  seedsEarned: number;
}>> => {
  try {
    const exerciseCompletionsRef = collection(db, 'forests', forestId, 'exerciseCompletions');
    const q = query(exerciseCompletionsRef, where('userId', '==', studentId));
    const querySnapshot = await getDocs(q);
    
    const completions = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        exerciseId: data.exerciseId,
        exerciseDay: data.day || data.exerciseDay,
        completedAt: data.completedAt?.toDate() || new Date(),
        uploadedFiles: data.uploadedFiles || [],
        seedsEarned: data.seedsEarned || 1,
      };
    });

    //Get exercise titles
    const forest = await getForestByIdDirect(forestId);
    if (forest) {
      const exercises = await getDailyExercisesByGroup(forest.dailyExerciseGroupId);
      const exerciseMap = new Map(exercises.map(ex => [ex.id, ex.title]));
      
      return completions.map(completion => ({
        ...completion,
        exerciseTitle: exerciseMap.get(completion.exerciseId) || `Day ${completion.exerciseDay} Exercise`
      }));
    }

    return completions.map(completion => ({
      ...completion,
      exerciseTitle: `Day ${completion.exerciseDay} Exercise`
    }));
  } catch (error) {
    console.error('Error getting detailed student exercise completions:', error);
    return [];
  }
};

//Export forest summary
export const generateForestExportData = async (forestId: string): Promise<{
  forestInfo: Forest;
  students: any[];
  analytics: any;
  exerciseMatrix: any[];
  waterActions: WaterAction[];
}> => {
  try {
    const [forestInfo, students, analytics, waterActions] = await Promise.all([
      getForestByIdDirect(forestId),
      getForestStudents(forestId),
      getForestAnalytics(forestId),
      getForestWaterActions(forestId)
    ]);

    //Exercise completion matrix
    const exerciseMatrix: any[] = [];
    if (forestInfo) {
      const exercises = await getDailyExercisesByGroup(forestInfo.dailyExerciseGroupId);
      
      for (const student of students) {
        const completions = await getStudentExerciseCompletions(forestId, student.uid);
        const completionMap = new Map(completions.map(c => [c.exerciseDay || c.day, true]));
        
        const studentRow: any = {
          studentId: student.uid,
          studentName: `${student.firstName} ${student.lastName}`,
          username: student.username,
          totalExercisesCompleted: completions.length,
        };

        //Daily stats
        exercises.forEach(exercise => {
          studentRow[`day${exercise.day}`] = completionMap.has(exercise.day) ? 'Completed' : 'Not Completed';
        });

        exerciseMatrix.push(studentRow);
      }
    }

    return {
      forestInfo: forestInfo!,
      students,
      analytics,
      exerciseMatrix,
      waterActions
    };
  } catch (error) {
    console.error('Error generating forest export data:', error);
    throw error;
  }
};



//Get Avail Exercises
export const getAvailableExercisesForCurrentTime = async (
  forestId: string,
  currentTime: Date = new Date()
): Promise<DailyExercise[]> => {
  try {
    //Get forest settings
    const forestDoc = await getDoc(doc(db, 'forests', forestId));
    if (!forestDoc.exists()) {
      throw new Error('Forest not found');
    }
    
    const forestData = forestDoc.data();
    const forest: Forest = {
      ...forestData,
      id: forestId,
      startDate: forestData.startDate?.toDate ? forestData.startDate.toDate() : forestData.startDate || new Date(),
      endDate: forestData.endDate?.toDate ? forestData.endDate.toDate() : forestData.endDate || new Date(),
      createdAt: forestData.createdAt?.toDate ? forestData.createdAt.toDate() : forestData.createdAt || new Date(),
      updatedAt: forestData.updatedAt?.toDate ? forestData.updatedAt.toDate() : forestData.updatedAt || new Date(),
    } as Forest;

    //Get exercises
    const exercises = await getDailyExercisesByGroup(forest.dailyExerciseGroupId);
    
    //Calculate current game day/time
    const startTime = forest.startDate.getTime();
    const now = currentTime.getTime();
    
    console.log(`Exercise Debug for "${forest.name}":`, {
      startTime: new Date(startTime).toLocaleTimeString(),
      currentTime: new Date(now).toLocaleTimeString(),
      gameStarted: now >= startTime,
      isQuickGame: forest.isQuickGame,
      gameDuration: forest.gameDuration
    });
    
    if (now < startTime) {
      console.log('Love the enthusiasm but game has not started yet');
      return [];
    }
    
    const timeElapsed = now - startTime;
    let dayDuration: number;
    
    //Calculate day duration
    if (forest.isQuickGame) {
      dayDuration = 60 * 1000;
    } else {
      dayDuration = 24 * 60 * 60 * 1000;
    }
    
    const currentDay = Math.floor(timeElapsed / dayDuration) + 1;
    const timeToNextDay = dayDuration - (timeElapsed % dayDuration);
    
    console.log(`Time Calculation:`, {
      timeElapsed: `${Math.round(timeElapsed / (60 * 1000))} minutes`,
      dayDuration: forest.isQuickGame ? '1 minute' : '24 hours',
      currentDay: currentDay,
      maxDay: forest.gameDuration,
      timeToNextDay: forest.isQuickGame ? `${Math.round(timeToNextDay / 1000)} seconds` : `${Math.round(timeToNextDay / (60 * 60 * 1000))} hours`,
      totalExercises: exercises.length
    });
    
    //Return exercises avail
    const availableExercises = exercises.filter(exercise => exercise.day <= currentDay);
    
    console.log(`Exercise Availability:`, {
      currentDay,
      availableExerciseIds: availableExercises.map(ex => `Day ${ex.day}: ${ex.title}`),
      nextExercise: exercises.find(ex => ex.day === currentDay + 1)?.title || 'None',
      gameMode: forest.isQuickGame ? 'Quick (1min/day)' : 'Normal (24h/day)'
    });
    
    return availableExercises;
  } catch (error) {
    console.error('Error getting available exercises:', error);
    throw error;
  }
};

//Quick game exercises
export const subscribeToExerciseAvailability = (
  forestId: string,
  onUpdate: (exercises: DailyExercise[]) => void
): (() => void) => {
  let intervalId: NodeJS.Timeout;
  
  const checkExercises = async () => {

    if (typeof window !== 'undefined') {
    const { activityManager } = await import('./activityManager');
    if (!activityManager.isActive()) {
      console.log('Skipping exercise check - inactive');
      return;
    }
  }
    try {
      const exercises = await getAvailableExercisesForCurrentTime(forestId);
      onUpdate(exercises);
    } catch (error) {
      console.error('Error checking exercise availability:', error);
    }
  };
  
  checkExercises();
  
  const forest = async () => {
    try {
      const forestDoc = await getDoc(doc(db, 'forests', forestId));
      if (forestDoc.exists()) {
        const forestData = forestDoc.data();
        const isQuickGame = forestData.isQuickGame || false;
        
        const interval = isQuickGame ? 30000 : 300000;
        intervalId = setInterval(checkExercises, interval);
        
        console.log(`Started exercise availability monitoring (${isQuickGame ? '30s' : '5min'} intervals)`);
      }
    } catch (error) {
      console.error('Error setting up exercise monitoring:', error);
    }
  };
  
  forest();
  
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      console.log('Stopped exercise availability monitoring');
    }
  };
};


//Helper
export const getCurrentGameDay = (forest: Forest, currentTime: Date = new Date()): number => {
  const startTime = forest.startDate.getTime();
  const now = currentTime.getTime();
  
  if (now < startTime) {
    return 0;
  }
  
  const timeElapsed = now - startTime;
  let dayDuration: number;
  
  if (forest.isQuickGame) {
    dayDuration = 60 * 1000;
  } else {
    dayDuration = 24 * 60 * 60 * 1000;
  }
  
  const currentDay = Math.floor(timeElapsed / dayDuration) + 1;
  return Math.min(currentDay, forest.gameDuration);
};


/******** TREES **********/

export const plantTree = async (
  forestId: string,
  planterId: string,
  planterName: string,
  position: { x: number; y: number },
  chosenColor?: string
): Promise<Tree> => {
  console.log(`Attempting to plant tree at (${position.x}, ${position.y})`);
  try {
    const now = new Date();
    const treeId = `${position.x}-${position.y}`;
    const treeRef = doc(db, 'forests', forestId, 'trees', treeId);
    
    //Checks quick game
    const forestDoc = await getDoc(doc(db, 'forests', forestId));
    if (!forestDoc.exists()) {
      throw new Error('Forest not found');
    }
    
    const forestData = forestDoc.data();
    const forest: Forest = {
      ...forestData,
      id: forestId,
      startDate: forestData.startDate?.toDate ? forestData.startDate.toDate() : forestData.startDate || new Date(),
      endDate: forestData.endDate?.toDate ? forestData.endDate.toDate() : forestData.endDate || new Date(),
      createdAt: forestData.createdAt?.toDate ? forestData.createdAt.toDate() : forestData.createdAt || new Date(),
      updatedAt: forestData.updatedAt?.toDate ? forestData.updatedAt.toDate() : forestData.updatedAt || new Date(),
    } as Forest;

    //Maturity time calculation
    let maturityTimeMs: number;
    if (forest.isQuickGame) {
      //Quick game
      maturityTimeMs = forest.gameDuration * 60 * 1000;
      console.log(`Quick Game tree: will mature in ${forest.gameDuration} minutes`);
    } else {
      //Normal
      maturityTimeMs = forest.gameDuration * 24 * 60 * 60 * 1000;
      console.log(`Normal tree: will mature in ${forest.gameDuration} days`);
    }
    
    const estimatedMaturityDate = new Date(now.getTime() + maturityTimeMs);
    
    const result = await runTransaction(db, async (transaction) => {
      //Check if tile is already planted
      const existingTree = await transaction.get(treeRef);
      
      if (existingTree.exists()) {
        const existingData = existingTree.data();
        console.log(`CONFLICT: Tile already planted by ${existingData.planterName}`);
        throw new Error('TILE_OCCUPIED');
      }
      
      //Create tree data
      const treeData = {
        forestId,
        planterId,
        planterName,
        position,
        plantedAt: serverTimestamp(),
        maturityStage: 0,
        waterReceived: 0,
        estimatedMaturityDate: Timestamp.fromDate(estimatedMaturityDate),
        chosenColor: chosenColor || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      //Atomic write
      transaction.set(treeRef, treeData);
      
      console.log(`SUCCESS: Tree planted by ${planterName} at (${position.x}, ${position.y})`);
      
      return {
        id: treeId,
        forestId,
        planterId,
        planterName,
        position,
        plantedAt: now,
        maturityStage: 0,
        waterReceived: 0,
        estimatedMaturityDate,
        chosenColor: chosenColor || undefined,
        createdAt: now,
        updatedAt: now,
      } as Tree;
    });
    
    return result;
    
  } catch (error) {
    if (error instanceof Error && error.message === 'TILE_OCCUPIED') {
      console.log(`Planting failed: Someone else planted at (${position.x}, ${position.y})`);
      throw new Error('TILE_OCCUPIED');
    }
    
    console.error('Unexpected planting error:', error);
    throw error;
  }
};


export const checkPlantingConflicts = (
  x: number, 
  y: number, 
  trees: Tree[],
  ghostUsers?: UserPresence[]
): { canPlant: boolean; reason?: string; conflictingUsers?: string[] } => {
  //Check if tree already exists
  const existingTree = trees.find(tree => tree.position.x === x && tree.position.y === y);
  if (existingTree) {
    return { 
      canPlant: false, 
      reason: `${existingTree.planterName} already planted here`,
      conflictingUsers: [existingTree.planterName]
    };
  }
  
  //Check if ghost users are standing on tile
  if (ghostUsers) {
    const usersAtPosition = ghostUsers.filter(user => user.position.x === x && user.position.y === y);
    if (usersAtPosition.length > 0) {
      //Allow planting race condition
      return { 
        canPlant: true, 
        reason: `${usersAtPosition.map(u => u.username).join(', ')} nearby`,
        conflictingUsers: usersAtPosition.map(u => u.username)
      };
    }
  }
  
  return { canPlant: true };
};


export const waterTree = async (
  forestId: string,
  treeId: string,
  watererId: string,
  watererName: string
): Promise<Tree> => {
  try {
    //Get forest data
    const forestDoc = await getDoc(doc(db, 'forests', forestId));
    if (!forestDoc.exists()) {
      throw new Error('Forest not found');
    }
    
    const forestData = forestDoc.data();
    const forest: Forest = {
      ...forestData,
      id: forestId,
      startDate: forestData.startDate?.toDate ? forestData.startDate.toDate() : forestData.startDate || new Date(),
      endDate: forestData.endDate?.toDate ? forestData.endDate.toDate() : forestData.endDate || new Date(),
      createdAt: forestData.createdAt?.toDate ? forestData.createdAt.toDate() : forestData.createdAt || new Date(),
      updatedAt: forestData.updatedAt?.toDate ? forestData.updatedAt.toDate() : forestData.updatedAt || new Date(),
    } as Forest;

    const treeRef = doc(db, 'forests', forestId, 'trees', treeId);
    const treeDoc = await getDoc(treeRef);
    
    if (!treeDoc.exists()) {
      throw new Error('Tree not found');
    }
    
    const treeData = treeDoc.data();
    const now = new Date();
    
    //Already fully mature?
    if (treeData.maturityStage >= 5) {
      console.log('Tree is already fully mature, water has no effect');
      return {
        id: treeId,
        ...treeData,
        plantedAt: treeData.plantedAt.toDate(),
        estimatedMaturityDate: treeData.estimatedMaturityDate.toDate(),
        createdAt: treeData.createdAt.toDate(),
        updatedAt: treeData.updatedAt.toDate(),
      } as Tree;
    }

    //Watering Mechanics
    const currentEstimatedMaturityTime = treeData.estimatedMaturityDate.toDate().getTime();
    const remainingTimeToMaturity = Math.max(0, currentEstimatedMaturityTime - now.getTime());
    
    //water reduction
    const isOwnTree = treeData.planterId === watererId;
    const waterEffect = isOwnTree ? GAME_CONFIG.SELF_WATER_EFFECT : GAME_CONFIG.OTHER_WATER_EFFECT;
    const timeReduction = remainingTimeToMaturity * waterEffect; //NOTE: This is set in gameConfig! Currently 7% and 15% as of 09/11/2025
    const newRemainingTime = remainingTimeToMaturity - timeReduction;
    const newEstimatedMaturityTime = now.getTime() + newRemainingTime;
    
    //Calculate water effect percentage for display
    const effectPercentage = remainingTimeToMaturity > 0 
      ? (timeReduction / remainingTimeToMaturity) * 100 
      : 0;

    //Update tree with new maturity time & water count
    const updates = {
      waterReceived: treeData.waterReceived + 1,
      estimatedMaturityDate: Timestamp.fromDate(new Date(newEstimatedMaturityTime)),
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(treeRef, updates);
    
    //Record water action
    const waterActionsCollection = collection(db, 'forests', forestId, 'waterActions');
    await addDoc(waterActionsCollection, {
      fromUserId: watererId,
      fromUserName: watererName,
      toUserId: treeData.planterId,
      treeId,
      amount: 1,
      timestamp: serverTimestamp(),
    });
    
    //Log the water effect
    const timeReductionDisplay = forest.isQuickGame 
      ? `${Math.round(timeReduction / 1000)} seconds`
      : `${Math.round(timeReduction / (60 * 1000))} minutes`;
    
    const remainingDisplay = forest.isQuickGame
      ? `${Math.round(newRemainingTime / 1000)} seconds`
      : `${Math.round(newRemainingTime / (60 * 1000))} minutes`;

    console.log(`Water Effect:`, {
      treeId,
      waterCount: treeData.waterReceived + 1,
      effectPercentage: `${effectPercentage.toFixed(1)}%`,
      timeReduction: timeReductionDisplay,
      remainingTime: remainingDisplay,
      isOwnTree: treeData.planterId === watererId,
      gameMode: forest.isQuickGame ? 'Quick' : 'Normal'
    });
    
    return {
      id: treeId,
      ...treeData,
      waterReceived: treeData.waterReceived + 1,
      estimatedMaturityDate: new Date(newEstimatedMaturityTime),
      plantedAt: treeData.plantedAt.toDate(),
      createdAt: treeData.createdAt.toDate(),
      updatedAt: now,
    } as Tree;
    
  } catch (error) {
    console.error('Error watering tree:', error);
    throw error;
  }
};

export const getForestTrees = async (forestId: string): Promise<Tree[]> => {
  try {
    //Subcollection query
    const treesCollection = collection(db, 'forests', forestId, 'trees');
    const q = query(treesCollection, orderBy('plantedAt', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const trees: Tree[] = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        plantedAt: data.plantedAt.toDate(),
        estimatedMaturityDate: data.estimatedMaturityDate.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    }) as Tree[];
    
    console.log(`Loaded ${trees.length} trees for forest ${forestId}`);
    return trees;
  } catch (error) {
    console.error('Error getting forest trees:', error);
    throw error;
  }
};

export const subscribeToForestTrees = (
  forestId: string, 
  onUpdate: (trees: Tree[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  try {
    const treesCollection = collection(db, 'forests', forestId, 'trees');
    const q = query(treesCollection, orderBy('plantedAt', 'asc'));
    /*
    const unsubscribe = onSnapshot(
      q, 
      (querySnapshot) => {
        const trees: Tree[] = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            plantedAt: data.plantedAt?.toDate() || new Date(),
            estimatedMaturityDate: data.estimatedMaturityDate?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          };
        }) as Tree[];
        
        console.log(`Real-time trees update: ${trees.length} trees`);
        onUpdate(trees);
      },
      (error) => {
        console.error('Real-time trees error:', error);
        onError(error);
      }
    );
*/

    /*ToDo: RemoveEventually*/
    const unsubscribe = tracedOnSnapshot(
      'trees',
      `forests/${forestId}/trees`,
      q,
      (querySnapshot) => {
        // (unchanged)
        const trees: Tree[] = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            plantedAt: data.plantedAt?.toDate() || new Date(),
            estimatedMaturityDate: data.estimatedMaturityDate?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          };
        }) as Tree[];
        console.log(`Real-time trees update: ${trees.length} trees`);
        onUpdate(trees);
      },
      (error) => {
        console.error('Real-time trees error:', error);
        onError(error);
      }
    );
/*RemoveEventually*/
    

    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to forest trees:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {}; //Return empty function if subscription fails
  }
};

//Tree growth automation
export const updateTreeGrowthStages = async (forestId: string): Promise<void> => {

  if (typeof window !== 'undefined') {
    const { activityManager } = await import('./activityManager');
    if (!activityManager.isActive()) {
      console.log(`Skipping tree growth update for forest ${forestId} - no active users`);
      return;
    }
  }

  try {
    const forestDoc = await getDoc(doc(db, 'forests', forestId));
    if (!forestDoc.exists()) {
      throw new Error('Forest not found');
    }
    
    const forestData = forestDoc.data();
    const forest: Forest = {
      ...forestData,
      id: forestId,
      startDate: forestData.startDate?.toDate ? forestData.startDate.toDate() : forestData.startDate || new Date(),
      endDate: forestData.endDate?.toDate ? forestData.endDate.toDate() : forestData.endDate || new Date(),
      createdAt: forestData.createdAt?.toDate ? forestData.createdAt.toDate() : forestData.createdAt || new Date(),
      updatedAt: forestData.updatedAt?.toDate ? forestData.updatedAt.toDate() : forestData.updatedAt || new Date(),
    } as Forest;

    const trees = await getForestTrees(forestId);
    const now = new Date();
    const batch = writeBatch(db);
    let updatedCount = 0;
    
    for (const tree of trees) {
      if (tree.maturityStage >= 5) continue;  //already mature
      
      const timeElapsed = now.getTime() - tree.plantedAt.getTime();
      
      //Est maturity time
      const treeMaturityTime = tree.estimatedMaturityDate.getTime() - tree.plantedAt.getTime();
      
      //Stage time
      const stageTime = treeMaturityTime / 6;
      
      //New stage
      const newStage = Math.min(5, Math.floor(timeElapsed / stageTime));
      
      if (newStage > tree.maturityStage) {
        const treeRef = doc(db, 'forests', forestId, 'trees', tree.id);
        batch.update(treeRef, {
          maturityStage: newStage,
          updatedAt: serverTimestamp(),
        });
        
        updatedCount++;
        
        //Log w/individual tree timing
        const elapsedDisplay = forest.isQuickGame 
          ? `${Math.round(timeElapsed / (60 * 1000))}min`
          : `${Math.round(timeElapsed / (60 * 60 * 1000))}h`;
          
        const maturityDisplay = forest.isQuickGame
          ? `${Math.round(treeMaturityTime / (60 * 1000))}min`
          : `${Math.round(treeMaturityTime / (24 * 60 * 60 * 1000))}d`;
          
        console.log(`Tree ${tree.id}: stage ${tree.maturityStage} → ${newStage}`, {
          elapsed: elapsedDisplay,
          totalMaturityTime: maturityDisplay,
          waterReceived: tree.waterReceived,
          mode: forest.isQuickGame ? 'Quick' : 'Normal'
        });
      }
    }
    
    if (updatedCount > 0) {
      await batch.commit();
      console.log(`Updated ${updatedCount} trees in forest "${forest.name}"`);
    }
  } catch (error) {
    console.error('Error updating tree growth stages:', error);
    throw error;
  }
};


export const getPlantingErrorMessage = (error: Error): string => {
  switch (error.message) {
    case 'TILE_OCCUPIED':
      return 'Whoops! Someone else just planted there!';
    case 'No seeds available':
      return 'You need seeds to plant a tree';
    case 'Invalid position':
      return 'You can only plant on dirt (plantable) tiles';
    default:
      return 'Planting failed - please try again';
  }
};


/******** ANIMALS **********/

export const getAnimals = async (): Promise<Animal[]> => {
  const animalsRef = collection(db, 'animals');
  const querySnapshot = await getDocs(animalsRef);
  
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Animal[];
};

export const getAnimalById = async (id: string): Promise<Animal | null> => {
  const animalRef = doc(db, 'animals', id);
  const animalSnap = await getDoc(animalRef);
  
  if (animalSnap.exists()) {
    return {
      id,
      ...animalSnap.data(),
    } as Animal;
  }
  
  return null;
};



/******** EXERCISES **********/

export const getDailyExercisesByGroup = async (groupId: string): Promise<DailyExercise[]> => {
  const exercisesRef = collection(db, 'dailyExercises');
  const q = query(exercisesRef, where('groupId', '==', groupId));
  const querySnapshot = await getDocs(q);
  
  const exercises = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate() || new Date(),
    updatedAt: doc.data().updatedAt?.toDate() || new Date(),
  })) as DailyExercise[];
  
  //Sort by day
  return exercises.sort((a, b) => a.day - b.day);
};

export const getDailyExerciseByGroupAndDay = async (groupId: string, day: number): Promise<DailyExercise | null> => {
  const exercisesRef = collection(db, 'dailyExercises');
  const q = query(
    exercisesRef, 
    where('groupId', '==', groupId),
    where('day', '==', day)
  );
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as DailyExercise;
  }
  
  return null;
};



/******** WATER ACTIONS **********/

export const getUserWaterActions = async (
  forestId: string,
  userId: string
): Promise<WaterAction[]> => {
  try {
    const waterActionsCollection = collection(db, 'forests', forestId, 'waterActions');
    const q = query(
      waterActionsCollection,
      where('toUserId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        timestamp: data.timestamp.toDate(),
      };
    }) as WaterAction[];
  } catch (error) {
    console.error('Error getting user water actions:', error);
    throw error;
  }
};



/******** DB CLEANUP **********/
//ToDo: Remove before release

export const clearAllCollections = async (): Promise<void> => {
  console.log('Clearing all collections...');
  
  try {
    const collections = ['users', 'forests', 'animals', 'dailyExercises'];
    
    for (const collectionName of collections) {
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      
      if (!snapshot.empty) {
        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        console.log(`Cleared ${snapshot.docs.length} documents from ${collectionName}`);
      }
    }
    
    //Clear any tree subcollections
    const forestsSnapshot = await getDocs(collection(db, 'forests'));
    for (const forestDoc of forestsSnapshot.docs) {
      const treesSnapshot = await getDocs(collection(db, 'forests', forestDoc.id, 'trees'));
      if (!treesSnapshot.empty) {
        const batch = writeBatch(db);
        treesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        console.log(`Cleared ${treesSnapshot.docs.length} trees from forest ${forestDoc.id}`);
      }
    }
    
    console.log('Database cleared successfully!');
  } catch (error) {
    console.error('Error clearing database:', error);
    throw error;
  }
};



/******** INITIALIZATION **********/

export const initializeDefaultData = async (): Promise<void> => {
  try {
    const lockRef = doc(db, 'system', 'initialization_lock');
    
    await runTransaction(db, async (transaction) => {
      const lockDoc = await transaction.get(lockRef);
      
      if (lockDoc.exists() && lockDoc.data().isInitializing) {
        throw new Error('INITIALIZATION_IN_PROGRESS');
      }
      
      const animalsSnapshot = await getDocs(collection(db, 'animals'));
      
      if (!animalsSnapshot.empty) {
        return;
      }
      
      transaction.set(lockRef, {
        isInitializing: true,
        startedAt: serverTimestamp()
      });
    });
    
    const animalsSnapshot = await getDocs(collection(db, 'animals'));
    
    if (animalsSnapshot.empty) {
      const defaultAnimals: Omit<Animal, 'id'>[] = [
        {
          name: 'Squirrel',
          shortDescription: 'Quick and resourceful forest dweller',
          longDescription: 'Squirrels are known for their incredible memory and planning abilities. They can remember thousands of hiding spots for their winter food storage. Just like you, they work hard to prepare for the future and help their forest community thrive.',
          type: 'mammal',
          subtype: 'rodent',
          metadata: {
            personality: 'energetic, organized, forward-thinking',
            strengths: ['planning', 'memory', 'agility'],
            learning_style: 'kinesthetic',
          },
          imagePath: '/assets/images/animals/squirrel.png',
          avatarPaths: {
            up: '/assets/images/avatars/squirrel/up.png',
            down: '/assets/images/avatars/squirrel/down.png',
            left: '/assets/images/avatars/squirrel/left.png',
            right: '/assets/images/avatars/squirrel/right.png',
          },
        },
        {
          name: 'Elephant',
          shortDescription: 'Wise and caring gentle giant',
          longDescription: 'Elephants are incredibly intelligent and empathetic animals. They have amazing memories and strong family bonds. They help each other and even other species in times of need. Like elephants, you have the power to be a gentle leader and help others in your community.',
          type: 'mammal',
          subtype: 'pachyderm',
          metadata: {
            personality: 'wise, empathetic, patient, leadership-oriented',
            strengths: ['memory', 'emotional intelligence', 'cooperation'],
            learning_style: 'collaborative',
          },
          imagePath: '/assets/images/animals/elephant.png',
          avatarPaths: {
            up: '/assets/images/avatars/elephant/up.png',
            down: '/assets/images/avatars/elephant/down.png',
            left: '/assets/images/avatars/elephant/left.png',
            right: '/assets/images/avatars/elephant/right.png',
          },
        },
        {
          name: 'Red Panda',
          shortDescription: 'Playful and unique forest explorer',
          longDescription: 'Red pandas are curious, independent, and adaptable creatures. They love to explore and are excellent climbers. Each red panda has its own unique personality and way of solving problems. Like red pandas, you bring your own special perspective to everything you do.',
          type: 'mammal',
          subtype: 'carnivore',
          metadata: {
            personality: 'curious, independent, adaptable, creative',
            strengths: ['exploration', 'problem-solving', 'independence'],
            learning_style: 'exploratory',
          },
          imagePath: '/assets/images/animals/red-panda.png',
          avatarPaths: {
            up: '/assets/images/avatars/red-panda/up.png',
            down: '/assets/images/avatars/red-panda/down.png',
            left: '/assets/images/avatars/red-panda/left.png',
            right: '/assets/images/avatars/red-panda/right.png',
          },
        },
        {
          name: 'Dinosaur',
          shortDescription: 'Ancient wisdom meets modern adventure',
          longDescription: 'Dinosaurs represent the incredible diversity of life and the importance of adaptation. Though they lived long ago, they teach us about resilience, evolution, and how life finds a way to thrive. Like dinosaurs, you can face challenges with strength and adapt to new situations.',
          type: 'reptile',
          subtype: 'prehistoric',
          metadata: {
            personality: 'resilient, strong, adaptive, persistent',
            strengths: ['resilience', 'strength', 'historical perspective'],
            learning_style: 'visual',
          },
          imagePath: '/assets/images/animals/dinosaur.png',
          avatarPaths: {
            up: '/assets/images/avatars/dinosaur/up.png',
            down: '/assets/images/avatars/dinosaur/down.png',
            left: '/assets/images/avatars/dinosaur/left.png',
            right: '/assets/images/avatars/dinosaur/right.png',
          },
        },
        {
          name: 'Gecko',
          shortDescription: 'Agile and detail-oriented climber',
          longDescription: 'Geckos are remarkably agile and have an amazing ability to stick to almost any surface. They pay attention to small details and are very patient hunters. Like geckos, you can achieve great things by paying attention to details and being persistent in reaching your goals.',
          type: 'reptile',
          subtype: 'lizard',
          metadata: {
            personality: 'detail-oriented, patient, agile, focused',
            strengths: ['attention to detail', 'patience', 'precision'],
            learning_style: 'methodical',
          },
          imagePath: '/assets/images/animals/gecko.png',
          avatarPaths: {
            up: '/assets/images/avatars/gecko/up.png',
            down: '/assets/images/avatars/gecko/down.png',
            left: '/assets/images/avatars/gecko/left.png',
            right: '/assets/images/avatars/gecko/right.png',
          },
        },
      ];

      const batch = writeBatch(db);
      for (const animal of defaultAnimals) {
        const animalRef = doc(collection(db, 'animals'));
        batch.set(animalRef, animal);
      }
      await batch.commit();
      
      const lockRef = doc(db, 'system', 'initialization_lock');
      await updateDoc(lockRef, {
        isInitializing: false,
        completedAt: serverTimestamp()
      });
      
      console.log('Default animals created successfully');
    }

    const forestsSnapshot = await getDocs(collection(db, 'forests'));
    if (forestsSnapshot.empty) {
      const sampleForest: Omit<Forest, 'id'> = {
        name: 'Sample Classroom Forest',
        slug: 'sample-forest',
        classCode: 'SAMPLE123',
        teacherId: 'sample-teacher-id',
        dailyExerciseGroupId: '1',
        gameDuration: 10,
        startDate: new Date(),
        endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, 'forests'), {
        ...sampleForest,
        startDate: serverTimestamp(),
        endDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      console.log('Sample forest created successfully');
    }

    //Sample daily exercises
    const exercisesSnapshot = await getDocs(collection(db, 'dailyExercises'));
    if (exercisesSnapshot.empty) {
      const sampleExercises: Omit<DailyExercise, 'id'>[] = [
        {
          groupId: '1',
          day: 1,
          mediaType: 2,
          mediaUrl: '',
          title: 'Welcome to Your Forest Journey',
          instructions: 'Welcome to your community forest! Today, take a moment to think about how our environment gives us hope. Look around your real environment - what do you notice about the resiliency of plants and trees? Write down three things that give you hope.',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          groupId: '1',
          day: 2,
          mediaType: 1,
          mediaUrl: 'https://www.youtube.com/watch?v=9JmN8_zUY1w',
          title: 'Awesome big wave at Mavs',
          instructions: 'Watch this video about how forests work together as a community. Think about how the trees, animals, and plants all help each other. I hope this is the right URL, would be a bummer if someone accidentally added a surfing video.',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          groupId: '1',
          day: 3,
          mediaType: 3,
          mediaUrl: 'https://docs.google.com/presentation/d/1fLJvPOvibcCUpJ9ES44_cdoX5Hb7LpDaloGWz5FbUEM/edit?slide=id.gec41538503_0_399#slide=id.gec41538503_0_399',
          title: 'The Water Cycle, or maybe DAOs',
          instructions: 'Explore this presentation about how water moves through forests. Pay attention to how trees help the water cycle. It IS possible someone switched out my slides with the DAO report, that would be a bummer.',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          groupId: '1',
          day: 4,
          mediaType: 2,
          mediaUrl: 'https://docs.google.com/presentation/d/1fLJvPOvibcCUpJ9ES44_cdoX5Hb7LpDaloGWz5FbUEM/edit?slide=id.gec41538503_0_399#slide=id.gec41538503_0_399',
          title: 'Cooperation in Nature',
          instructions: 'Think about how you have helped a classmate this week. Write a short paragraph about a time when working together made something better. How is this similar to how trees and animals help each other in a forest?',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          groupId: '1',
          day: 5,
          mediaType: 1,
          mediaUrl: 'https://www.youtube.com/watch?v=9JmN8_zUY1w',
          title: 'Growing Strong Communities',
          instructions: 'Watch this video about how communities grow strong when everyone contributes. Think about your role in your classroom community.',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ];

      //Add sample exercises to Firestore
      for (const exercise of sampleExercises) {
        await addDoc(collection(db, 'dailyExercises'), {
          ...exercise,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      
      console.log('Sample daily exercises created successfully');
    }
    
  } catch (error) {
    if (error instanceof Error && error.message === 'INITIALIZATION_IN_PROGRESS') {
      console.log('Initialization already in progress, skipping');
      await new Promise(resolve => setTimeout(resolve, 2000));
    } else {
      console.error('ERROR: error in initializeDefaultData:', error);
      throw error;
    }
  }
};


/******** HELPERS  *********/

/*DEPRICATED 09/14/20205*/

/*
export const updateAllActiveForests = async (): Promise<void> => {
  
  if (typeof window !== 'undefined') {
    const { activityManager } = await import('./activityManager');
    if (!activityManager.isActive()) {
      console.log('Skipping forest updates - no active users');
      return;
    }
  }

  try {
    //Get all active forests
    const forestsRef = collection(db, 'forests');
    const q = query(forestsRef, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    const forests = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate?.toDate() || new Date(),
        endDate: data.endDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Forest;
    });
    
    console.log(`Updating ${forests.length} active forests (${forests.filter(f => f.isQuickGame).length} quick games)`);
    
    //Update inidiv forests
    const updatePromises = forests.map(async (forest) => {
      try {
        //Update tree growth
        await updateTreeGrowthStages(forest.id);
        
        //Deactivated?
        const now = new Date();
        const shouldDeactivate = now > forest.endDate;
        
        if (shouldDeactivate) {
          console.log(`${forest.isQuickGame ? 'Quick' : 'Normal'} forest "${forest.name}" has ended, deactivating...`);
          await updateDoc(doc(db, 'forests', forest.id), {
            isActive: false,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (error) {
        console.error(`Error updating forest ${forest.id}:`, error);
      }
    });
    
    await Promise.all(updatePromises);
    
  } catch (error) {
    console.error('Error updating active forests:', error);
  }
};

*/


/*
//BG monitoring
export const startBackgroundMonitoring = () => {
  //10 sec intervals
  const intervalId = setInterval(async () => {
    try {
      await updateAllActiveForests();
    } catch (error) {
      console.error('Error in background forest update:', error);
    }
  }, 10000);
  
  console.log('Started background forest monitoring (10s intervals)');
  
  return intervalId;
};
*/